// Considered: PickerShellMolecule's own row anatomy (none — every existing caller supplies
// its own row) and SphereTypeSelectMolecule's checklist option (rejected: that row is a
// multi-select checkbox for the applicability picker's family/type axes; this row is a
// single-select radio for "what does this type inherit from", and carries facts
// (family-vs-instantiable, current parent, platform ownership, what it brings, a caution)
// none of that shape needs). See spec Design System Reuse Audit.
//
// One row of the parent picker (`place.picker`, `type-composer.jsx:1084-1101`) — a candidate
// family the type could inherit from instead of its current one.
import React from 'react';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import type { ParentOption } from '../../organisms/studio/SphereTypeComposerOrganism';
import './ParentOptionRowMolecule.scss';

export interface ParentOptionRowMoleculeProps {
  option: ParentOption;
  /** This option is the type's current parent — the row draws its radio filled. */
  selected: boolean;
  t: (key: string, params?: Record<string, unknown>) => string;
}

/**
 * The CONTENT of one option. `PickerShellMolecule` owns the `role="option"` wrapper, its
 * `aria-selected` and its activation — an option may hold no interactive descendant, so
 * this row is markup only. The filled dot stays: it says which parent is in force
 * without relying on the option's selected colour alone (ux-craft A6).
 */
const ParentOptionRowMolecule: React.FC<ParentOptionRowMoleculeProps> = ({ option, selected, t }) => {
  return (
    <div
      className="parent-option-row-molecule"
      data-on={selected ? '1' : undefined}
      data-testid={`sphere-type-placement-parent-option-${option.schemaId}`}
    >
      <span
        className="parent-option-row-molecule__radio"
        data-datum="place.radio-indicator"
        data-kind="radio-indicator"
        aria-hidden="true"
      />
      <GlyphAtom
        variant="tile"
        glyph={option.glyph ?? undefined}
        glyphColor={option.glyphColor ?? undefined}
        fallbackText={option.name}
        aria-hidden
        data-datum="place.tile#2"
        data-kind="tile"
      />
      <span className="parent-option-row-molecule__main">
        <span className="parent-option-row-molecule__top" data-datum="place.label" data-kind="label">
          <strong data-datum="place.pp-name" data-kind="strong">{option.name}</strong>
          <span className="parent-option-row-molecule__pill" data-datum="place.a-family#2" data-kind="pill">
            {option.isAbstract
              ? t('studio.typeEditor.placement.parentIsFamily')
              : t('studio.typeEditor.placement.parentMakesSpheres')}
          </span>
          {option.isCurrent && (
            <span className="parent-option-row-molecule__chip" data-datum="place.where-it-is-now#2" data-kind="chip">
              {t('studio.typeEditor.placement.whereItIsNow')}
            </span>
          )}
          {option.isPlatform && (
            <span className="parent-option-row-molecule__pill parent-option-row-molecule__pill--platform" data-datum="place.platform" data-kind="pill">
              <i className="pi pi-lock" aria-hidden="true" />
              {t('studio.typeEditor.placement.platform')}
            </span>
          )}
        </span>
        {option.description && (
          <span className="parent-option-row-molecule__desc" data-datum="place.pp-desc#2" data-kind="text">
            {option.description}
          </span>
        )}
        <span className="parent-option-row-molecule__brings" data-datum="place.brings-nothing-of-its-own" data-kind="text">
          {option.brings
            ? `${t('studio.typeEditor.placement.bringsPrefix')} ${option.brings}`
            : t('studio.typeEditor.placement.bringsNothing')}
        </span>
        {option.caution && (
          <span className="parent-option-row-molecule__caution" data-datum="place.pp-caution" data-kind="note">
            {option.caution}
          </span>
        )}
      </span>
    </div>
  );
};

export default ParentOptionRowMolecule;
